import {
  useWsmFitmentAxesQuery,
  useWsmFitmentQualifiersQuery,
  useWsmFitmentSchemasQuery,
  WsmFitmentAxesQuery,
  WsmFitmentQualifiersQuery,
  WsmFitmentSchemasQuery,
} from "@dashboard/graphql";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import { useEffect, useState } from "react";

/** The API caps a page at 100; the vocabulary lists are walked whole in pages of that. */
const PAGE = 100;

interface Connection<TNode> {
  edges: Array<{ node: TNode }>;
  pageInfo: { hasNextPage: boolean; endCursor: string | null };
}

/**
 * The next page's edges appended to what is already on screen, each row once:
 * a refetch that lands mid-walk can hand back a page whose rows are already
 * here, and a row listed twice would render twice.
 */
export const appendConnection = <
  TConnection extends { edges: ReadonlyArray<{ node: { id: string } }> },
>(
  previous: TConnection | null | undefined,
  next: TConnection | null | undefined,
): TConnection | null => {
  if (!previous || !next) {
    return next ?? previous ?? null;
  }

  const seen = new Set(previous.edges.map(edge => edge.node.id));

  return {
    ...next,
    edges: [...previous.edges, ...next.edges.filter(edge => !seen.has(edge.node.id))],
  };
};

/**
 * Keeps fetching while the connection says there is more. The lists this walks
 * (axes, schemas, qualifiers) are a handful of rows on every store; the walk is
 * only insurance against the day one grows past a page.
 *
 * A page that fails stops the walk and is reported, not left "loading": the
 * cursor it failed on is remembered, and `retry` asks for it again.
 */
const useWalk = (
  pageInfo: Connection<unknown>["pageInfo"] | undefined,
  // Resolves whether the page arrived: the Dashboard's queries run with
  // errorPolicy "all", so a GraphQL failure resolves with no data rather than
  // rejecting, and a network failure rejects. Both are a failed page.
  fetchNext: (after: string) => Promise<boolean>,
) => {
  const after = pageInfo?.hasNextPage ? pageInfo.endCursor : null;
  const [failedAfter, setFailedAfter] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const failed = !!after && failedAfter === after;

  useEffect(() => {
    if (after && !failed) {
      fetchNext(after)
        .then(arrived => {
          if (!arrived) {
            setFailedAfter(after);
          }
        })
        .catch(() => setFailedAfter(after));
    }
    // fetchMore is stable for the life of the query; only the cursor matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [after, attempt]);

  return {
    failed,
    retry: () => {
      setFailedAfter(null);
      setAttempt(current => current + 1);
    },
  };
};

/**
 * What every vocabulary hook returns: the rows, `loading` while the first page
 * or the walk is in flight, and `error` when either failed, which ends
 * `loading`, so a screen shows its retry instead of a skeleton that never ends.
 */
const walkState = (
  query: { loading: boolean; error?: unknown; refetch: () => Promise<unknown> },
  walk: ReturnType<typeof useWalk>,
  connection: Connection<unknown> | null | undefined,
) => {
  // A first page that came back with rows and an error still shows its rows.
  const firstPageFailed = !!query.error && !connection;
  const error = firstPageFailed || walk.failed;

  return {
    loading: !error && (query.loading || !!connection?.pageInfo.hasNextPage),
    error,
    retry: () => {
      if (firstPageFailed) {
        query.refetch().catch(() => undefined);
      } else {
        walk.retry();
      }
    },
  };
};

export const useWsmFitmentAxes = () => {
  const query = useWsmFitmentAxesQuery({ variables: { first: PAGE } });
  const { data, fetchMore } = query;
  const walk = useWalk(data?.wsmFitmentAxes?.pageInfo, after =>
    fetchMore({
      variables: { first: PAGE, after },
      updateQuery: (previous, { fetchMoreResult }): WsmFitmentAxesQuery => ({
        ...previous,
        wsmFitmentAxes: appendConnection(previous.wsmFitmentAxes, fetchMoreResult?.wsmFitmentAxes),
      }),
    }).then(result => !!result.data?.wsmFitmentAxes),
  );

  return {
    axes: mapEdgesToItems(data?.wsmFitmentAxes) ?? [],
    ...walkState(query, walk, data?.wsmFitmentAxes),
  };
};

export const useWsmFitmentSchemas = () => {
  const query = useWsmFitmentSchemasQuery({ variables: { first: PAGE } });
  const { data, fetchMore } = query;
  const walk = useWalk(data?.wsmFitmentSchemas?.pageInfo, after =>
    fetchMore({
      variables: { first: PAGE, after },
      updateQuery: (previous, { fetchMoreResult }): WsmFitmentSchemasQuery => ({
        ...previous,
        wsmFitmentSchemas: appendConnection(
          previous.wsmFitmentSchemas,
          fetchMoreResult?.wsmFitmentSchemas,
        ),
      }),
    }).then(result => !!result.data?.wsmFitmentSchemas),
  );

  return {
    schemas: mapEdgesToItems(data?.wsmFitmentSchemas) ?? [],
    ...walkState(query, walk, data?.wsmFitmentSchemas),
  };
};

export const useWsmFitmentQualifiers = () => {
  const query = useWsmFitmentQualifiersQuery({ variables: { first: PAGE } });
  const { data, fetchMore } = query;
  const walk = useWalk(data?.wsmFitmentQualifiers?.pageInfo, after =>
    fetchMore({
      variables: { first: PAGE, after },
      updateQuery: (previous, { fetchMoreResult }): WsmFitmentQualifiersQuery => ({
        ...previous,
        wsmFitmentQualifiers: appendConnection(
          previous.wsmFitmentQualifiers,
          fetchMoreResult?.wsmFitmentQualifiers,
        ),
      }),
    }).then(result => !!result.data?.wsmFitmentQualifiers),
  );

  return {
    qualifiers: mapEdgesToItems(data?.wsmFitmentQualifiers) ?? [],
    ...walkState(query, walk, data?.wsmFitmentQualifiers),
  };
};
